import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X as XIcon, Plus as PlusIcon } from 'lucide-react';
import { listEmpresas } from '@lib/services/empresas.service';
import {
  invitarUsuario,
  listCuentasServicio,
  type CuentaServicio,
  listUsuarios,
  reinvitarUsuario,
  actualizarUsuario,
  asignarEmpresaAUsuario,
  quitarEmpresaDeUsuario,
  setPermisosUsuario,
} from '@lib/services/usuarios.service';
import { useAuthStore } from '@lib/store/auth.store';
import { ROL_LABELS, UserRole, type Empresa, type UsuarioListItem } from '@lib/types';
import {
  PERMISSION_CATALOG,
  ROLE_DEFAULT_VIEWS,
  VISTAS_DERIVADAS,
  permisosEfectivos,
  type ViewKey,
} from '@lib/permissions/catalog';
import { Spinner, TableSkeleton } from '@components/medicus-ui/Loaders';
import { ErrorInline } from '@components/ErrorInline';
import { EquipoSelect } from '@components/EquipoSelect';
import type { EquipoVenta } from '@lib/catalogs/equipos-venta';

export function UsuariosPage() {
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');
  /** Filtro por rol de la tabla (''=todos). Se aplica en cliente sobre lo que
   *  ya trajo la búsqueda, así el contador de cada rol refleja el resultado
   *  actual y cambiar de rol no dispara otro request. */
  const [filtroRol, setFiltroRol] = useState<UserRole | ''>('');
  const [showForm, setShowForm] = useState(false);

  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<UserRole>(UserRole.USUARIO_EMPRESA);
  const [empresaIds, setEmpresaIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activationLink, setActivationLink] = useState<string | null>(null);

  const empresas = useQuery({ queryKey: ['empresas-for-invite'], queryFn: () => listEmpresas() });

  // Integraciones que pidieron un token y quedaron esperando habilitación. El
  // provisioning JIT las crea INACTIVAS a propósito (un token sin identidad no
  // se habilita solo), pero hasta hoy eso no se veía en ningún lado: el
  // scorecard del Growth Dashboard estuvo cinco días devolviendo 401 y nos
  // enteramos de casualidad. La señal existía —24 asientos de acceso rechazado
  // en la auditoría— pero enterrada en /actividad.
  const servicio = useQuery({
    queryKey: ['cuentas-servicio'],
    queryFn: listCuentasServicio,
  });
  /** Las que todavía no pueden operar: les falta habilitación u organización. */
  const pendientesSv = useMemo(
    () => (servicio.data ?? []).filter((c) => c.falta.length > 0),
    [servicio.data],
  );

  /** Pestaña visible. Una cuenta de servicio es otro tipo de acceso, no otro usuario. */
  const [tab, setTab] = useState<'usuarios' | 'servicio'>('usuarios');

  const refrescarServicio = () => {
    qc.invalidateQueries({ queryKey: ['cuentas-servicio'] });
    qc.invalidateQueries({ queryKey: ['usuarios'] });
  };

  const habilitar = useMutation({
    mutationFn: (vars: { id: string; activo: boolean }) =>
      actualizarUsuario(vars.id, { activo: vars.activo }),
    onSuccess: refrescarServicio,
  });

  const asignarEmpresaServicio = useMutation({
    mutationFn: (vars: { usuarioId: string; empresaId: string }) =>
      asignarEmpresaAUsuario(vars.usuarioId, vars.empresaId),
    onSuccess: refrescarServicio,
  });

  const usuarios = useQuery({
    queryKey: ['usuarios', search],
    queryFn: () => listUsuarios({ search: search || undefined }),
  });

  // Cuántos usuarios hay de cada rol en el resultado actual. Alimenta el
  // desplegable del header (y deja ver de un vistazo cómo va la migración a
  // `user-tmk-medicus`). Se listan solo los roles presentes, en orden del
  // catálogo, para no ofrecer filtros que no devuelven nada.
  const conteoPorRol = useMemo(() => {
    const acc = new Map<UserRole, number>();
    for (const u of usuarios.data ?? []) acc.set(u.rol, (acc.get(u.rol) ?? 0) + 1);
    return acc;
  }, [usuarios.data]);

  const rolesPresentes = useMemo(
    () => (Object.keys(ROL_LABELS) as UserRole[]).filter((r) => conteoPorRol.has(r)),
    [conteoPorRol],
  );

  const usuariosVisibles = useMemo(
    () => (usuarios.data ?? []).filter((u) => !filtroRol || u.rol === filtroRol),
    [usuarios.data, filtroRol],
  );

  const invite = useMutation({
    mutationFn: invitarUsuario,
    onSuccess: (u) => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setActivationLink(u.activationUrl ?? null);
      setEmail('');
      setNombre('');
      setEmpresaIds([]);
      setError(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No se pudo invitar al usuario');
    },
  });

  const reinvitar = useMutation({
    mutationFn: reinvitarUsuario,
    onSuccess: (u) => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setActivationLink(u.activationUrl ?? null);
    },
  });

  const toggle = useMutation({
    mutationFn: (vars: { id: string; activo: boolean }) =>
      actualizarUsuario(vars.id, { activo: vars.activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });

  // Equipo de ventas (VEN-516): alimenta el filtro Equipo de Ventas/Comisiones.
  const cambiarEquipo = useMutation({
    mutationFn: (vars: { id: string; equipo: EquipoVenta | null }) =>
      actualizarUsuario(vars.id, { equipo: vars.equipo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });

  // Usuario de Binary del promotor. Es lo que habilita a esta persona a subir
  // padrones: con él se consulta `SA_PROMOTORES_EMPLEADORES` para saber de qué
  // empresas es promotor vigente.
  const setPromotor = useMutation({
    mutationFn: (vars: { id: string; binaryUsername: string }) =>
      actualizarUsuario(vars.id, { binaryUsername: vars.binaryUsername }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['usuarios'] });
    },
    // Un username inválido (el server lo valida con un regex) volvía a la
    // celda con el valor viejo y sin decir nada.
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No se pudo guardar el promotor');
    },
  });

  const asignar = useMutation({
    mutationFn: (vars: { usuarioId: string; empresaId: string }) =>
      asignarEmpresaAUsuario(vars.usuarioId, vars.empresaId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });

  const quitar = useMutation({
    mutationFn: (vars: { usuarioId: string; empresaId: string }) =>
      quitarEmpresaDeUsuario(vars.usuarioId, vars.empresaId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });

  // Editor de permisos por vista (overrides allow/deny) del usuario seleccionado.
  const [permisosTarget, setPermisosTarget] = useState<UsuarioListItem | null>(null);
  const setPermisos = useMutation({
    mutationFn: (vars: { id: string; allow: string[]; deny: string[] }) =>
      setPermisosUsuario(vars.id, { allow: vars.allow, deny: vars.deny }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setPermisosTarget(null);
    },
  });

  const empresasById = useMemo(() => {
    const m = new Map<string, Empresa>();
    (empresas.data ?? []).forEach((e) => m.set(e.id, e));
    return m;
  }, [empresas.data]);

  const rolesInvitables = useMemo(() => {
    // SuperAdmin puede crear cualquier rol Medicus (incluido el de solo
    // lectura) y también UE. Admin solo puede invitar UE.
    if (user?.rol === UserRole.SUPERADMIN_MEDICUS) {
      return [UserRole.ADMIN_MEDICUS, UserRole.USER_MEDICUS, UserRole.USUARIO_EMPRESA];
    }
    return [UserRole.USUARIO_EMPRESA];
  }, [user]);

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Usuarios</h1>
        <p className="text-element-mid">
          Invitá nuevos usuarios y gestioná sus accesos.
        </p>
      </header>

      {pendientesSv.length > 0 && tab !== 'servicio' && (
        <button
          type="button"
          onClick={() => setTab('servicio')}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            border: '1px solid #f0c869',
            background: '#fffaf0',
            borderRadius: 10,
            padding: '12px 14px',
            marginTop: '1rem',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 13, color: '#8a5a00', fontWeight: 600 }}>
            {pendientesSv.length === 1
              ? 'Hay 1 integración que todavía no puede operar'
              : `Hay ${pendientesSv.length} integraciones que todavía no pueden operar`}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--color-element-mid)',
              marginTop: 4,
            }}
          >
            Mientras les falte habilitación u organización, sus llamadas reciben 401 o 403. Tocá
            para verlas.
          </span>
        </button>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button
          type="button"
          className={tab === 'usuarios' ? 'btn btn-primary btn--sm' : 'btn btn-ghost btn--sm'}
          onClick={() => setTab('usuarios')}
        >
          Usuarios
        </button>
        <button
          type="button"
          className={tab === 'servicio' ? 'btn btn-primary btn--sm' : 'btn btn-ghost btn--sm'}
          onClick={() => setTab('servicio')}
        >
          Cuentas de servicio
          {pendientesSv.length > 0 && (
            <span
              style={{
                marginLeft: 6,
                background: '#e8a33d',
                color: '#fff',
                borderRadius: 10,
                padding: '0 6px',
                fontSize: 11,
              }}
            >
              {pendientesSv.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'servicio' && (
        <CuentasServicioTab
          onPermisos={(c) =>
            // El modal ya sabe editar overrides de cualquier usuario; la cuenta
            // de servicio se le pasa con la misma forma. No se busca en la lista
            // de usuarios porque esa está filtrada por el buscador.
            setPermisosTarget({
              id: c.id,
              nombre: c.nombre,
              email: c.email,
              rol: c.rol as UserRole,
              empresaIds: c.empresaIds,
              activo: c.activo,
              overrides: c.overrides,
            } as UsuarioListItem)
          }
          cuentas={servicio.data}
          cargando={servicio.isLoading}
          empresas={empresas.data ?? []}
          ocupado={habilitar.isPending || asignarEmpresaServicio.isPending}
          onToggle={(id, activo) => habilitar.mutate({ id, activo })}
          onAsignarEmpresa={(usuarioId, empresaId) =>
            asignarEmpresaServicio.mutate({ usuarioId, empresaId })
          }
        />
      )}

      {tab === 'usuarios' && (
        <>
      <div className="rowBetween" style={{ marginTop: '1rem' }}>
        <input
          className="input"
          style={{ maxWidth: 360 }}
          placeholder="Buscar por email o nombre…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          <span className="btn-font-style">
            {showForm ? 'Cancelar' : 'Invitar usuario'}
          </span>
        </button>
      </div>

      {showForm && (
        <form
          className="card"
          style={{ marginTop: '1rem' }}
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate({
              email,
              nombre: nombre || undefined,
              rol,
              empresaIds: rol === UserRole.USUARIO_EMPRESA ? empresaIds : undefined,
            });
          }}
        >
          <h2 className="heading-5">Invitar usuario</h2>
          <div className="modalGrid" style={{ marginTop: '0.75rem' }}>
            <label className="formField">
              <span>Email</span>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label className="formField">
              <span>Nombre (opcional)</span>
              <input
                className="input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </label>
            <label className="formField">
              <span>Rol</span>
              <select
                className="input"
                value={rol}
                onChange={(e) => setRol(e.target.value as UserRole)}
              >
                {rolesInvitables.map((r) => (
                  <option key={r} value={r}>
                    {ROL_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            {rol === UserRole.USUARIO_EMPRESA && (
              <label className="formField">
                <span>Organización(es) — Ctrl/Cmd para seleccionar varias</span>
                <select
                  className="input"
                  multiple
                  size={Math.min(6, empresas.data?.length ?? 3)}
                  value={empresaIds}
                  onChange={(e) =>
                    setEmpresaIds(Array.from(e.target.selectedOptions).map((o) => o.value))
                  }
                >
                  {empresas.data?.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombreComercial} ({emp.cuit})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {error && (
            <p className="status status-error" style={{ marginTop: '1rem' }}>
              {error}
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="btn btn-primary" type="submit" disabled={invite.isPending}>
              <span className="btn-font-style">
                {invite.isPending ? 'Invitando…' : 'Invitar'}
              </span>
            </button>
          </div>
        </form>
      )}

      {activationLink && (
        <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--color-success)' }}>
          <p className="status status-success">Invitación generada</p>
          <p className="text-element-mid" style={{ marginTop: '0.5rem' }}>
            Copiá este link y compartilo con el invitado (mientras no haya integración real de email):
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <input className="input" readOnly value={activationLink} />
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => navigator.clipboard.writeText(activationLink)}
            >
              Copiar
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '1rem' }}>
        {usuarios.isLoading && <TableSkeleton rows={6} />}
        {/* Las acciones de la tabla (reinvitar, activar, equipo, organizaciones,
            cuentas de servicio) fallaban en silencio: el server contestaba 403
            o 500 y el botón volvía a su estado normal como si hubiera salido. */}
        <ErrorInline
          error={
            [habilitar, asignarEmpresaServicio, reinvitar, toggle, cambiarEquipo, asignar, quitar].find(
              (m) => m.error,
            )?.error
          }
          fallback="No se pudo guardar el cambio del usuario."
          style={{ marginBottom: 8 }}
        />
        {usuarios.data && usuarios.data.length === 0 && (
          <p className="text-element-mid">Aún no hay usuarios.</p>
        )}
        {usuarios.data && usuarios.data.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>
                    Rol
                    <select
                      className={`thFilter${filtroRol ? ' thFilter--activo' : ''}`}
                      value={filtroRol}
                      onChange={(e) => setFiltroRol(e.target.value as UserRole | '')}
                      title="Filtrar por rol"
                      aria-label="Filtrar por rol"
                    >
                      <option value="">Todos ({usuarios.data?.length ?? 0})</option>
                      {rolesPresentes.map((r) => (
                        <option key={r} value={r}>
                          {ROL_LABELS[r]} ({conteoPorRol.get(r)})
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>Promotor (Binary)</th>
                  <th>Organizaciones</th>
                  <th>Equipo</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {usuariosVisibles.map((u) => (
                  <tr key={u.id}>
                    <td>{u.nombre}</td>
                    <td>{u.email}</td>
                    <td>{ROL_LABELS[u.rol]}</td>
                    <td>
                      <PromotorCell
                        valor={u.binaryUsername ?? ''}
                        onGuardar={(binaryUsername) =>
                          setPromotor.mutate({ id: u.id, binaryUsername })
                        }
                        busy={setPromotor.isPending}
                      />
                    </td>
                    <td style={{ minWidth: 240 }}>
                      <EmpresasCell
                        usuario={u}
                        empresasById={empresasById}
                        empresasAll={empresas.data ?? []}
                        onAsignar={(empresaId) =>
                          asignar.mutate({ usuarioId: u.id, empresaId })
                        }
                        onQuitar={(empresaId) =>
                          quitar.mutate({ usuarioId: u.id, empresaId })
                        }
                        busy={asignar.isPending || quitar.isPending}
                      />
                    </td>
                    <td>
                      <EquipoSelect
                        nombre={u.nombre}
                        rol={u.rol}
                        value={u.equipo}
                        disabled={cambiarEquipo.isPending}
                        onChange={(equipo) => cambiarEquipo.mutate({ id: u.id, equipo })}
                      />
                    </td>
                    <td>
                      {u.pendienteActivacion ? (
                        <span className="status status-warning">pendiente</span>
                      ) : u.activo ? (
                        <span className="status status-success">activo</span>
                      ) : (
                        <span className="status status-error">inactivo</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {u.pendienteActivacion ? (
                          <button
                            className="btn btn-link"
                            disabled={reinvitar.isPending && reinvitar.variables === u.id}
                            onClick={() => reinvitar.mutate(u.id)}
                          >
                            {reinvitar.isPending && reinvitar.variables === u.id ? (
                              <>
                                <Spinner size={12} /> Enviando…
                              </>
                            ) : (
                              'Reenviar invitación'
                            )}
                          </button>
                        ) : (
                          <button
                            className="btn btn-link"
                            disabled={toggle.isPending && toggle.variables?.id === u.id}
                            onClick={() =>
                              toggle.mutate({ id: u.id, activo: !u.activo })
                            }
                          >
                            {toggle.isPending && toggle.variables?.id === u.id ? (
                              <Spinner size={12} />
                            ) : u.activo ? (
                              'Desactivar'
                            ) : (
                              'Reactivar'
                            )}
                          </button>
                        )}
                        <button className="btn btn-link" onClick={() => setPermisosTarget(u)}>
                          Permisos
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {usuariosVisibles.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-element-mid">
                      Ningún usuario con el rol {ROL_LABELS[filtroRol as UserRole]}.{' '}
                      <button className="btn btn-link" onClick={() => setFiltroRol('')}>
                        Ver todos
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </div>
        </>
      )}

      {permisosTarget && (
        <PermisosModal
          usuario={permisosTarget}
          saving={setPermisos.isPending}
          error={setPermisos.error}
          onGuardar={(allow, deny) =>
            setPermisos.mutate({ id: permisosTarget.id, allow, deny })
          }
          onCerrar={() => setPermisosTarget(null)}
        />
      )}
    </section>
  );
}

/**
 * Celda de empresas para un usuario: chips con cada empresa asignada (con X
 * para quitar) + select para agregar una nueva.
 *
 * Solo tiene sentido para USUARIO_EMPRESA — los admins/SA acceden a todas.
 */
function EmpresasCell({
  usuario,
  empresasById,
  empresasAll,
  onAsignar,
  onQuitar,
  busy,
}: {
  usuario: UsuarioListItem;
  empresasById: Map<string, Empresa>;
  empresasAll: Empresa[];
  onAsignar: (empresaId: string) => void;
  onQuitar: (empresaId: string) => void;
  busy: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState('');

  if (usuario.rol !== UserRole.USUARIO_EMPRESA) {
    return <span className="text-element-mid" style={{ fontSize: 12 }}>Todas (admin)</span>;
  }

  const noAsignadas = empresasAll.filter((e) => !usuario.empresaIds.includes(e.id));

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {usuario.empresaIds.length === 0 && (
        <span className="text-element-mid" style={{ fontSize: 12 }}>Sin organizaciones</span>
      )}
      {usuario.empresaIds.map((eid) => {
        const e = empresasById.get(eid);
        const label = e ? e.nombreComercial : `${eid.slice(0, 8)}… (no encontrada)`;
        return (
          <span
            key={eid}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 6px 2px 8px',
              borderRadius: 12,
              fontSize: 11,
              background: e ? 'rgba(0,37,100,0.08)' : '#FEE2E2',
              color: e ? 'var(--color-primary-darker)' : '#7F1D1D',
              fontWeight: 500,
            }}
            title={e?.cuit ?? eid}
          >
            {label}
            <button
              type="button"
              disabled={busy}
              onClick={() => onQuitar(eid)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: busy ? 'wait' : 'pointer',
                padding: 0,
                display: 'inline-flex',
                opacity: 0.7,
              }}
              title="Quitar"
              aria-label="Quitar organización"
            >
              <XIcon size={11} />
            </button>
          </span>
        );
      })}

      {adding ? (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <select
            className="input"
            style={{ padding: '2px 6px', fontSize: 12, height: 24 }}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            disabled={busy}
          >
            <option value="">— elegir —</option>
            {noAsignadas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombreComercial} ({e.cuit})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '2px 8px', fontSize: 11 }}
            disabled={!pick || busy}
            onClick={() => {
              onAsignar(pick);
              setPick('');
              setAdding(false);
            }}
          >
            OK
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '2px 6px', fontSize: 11 }}
            onClick={() => {
              setAdding(false);
              setPick('');
            }}
          >
            <XIcon size={11} />
          </button>
        </span>
      ) : noAsignadas.length > 0 ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={busy}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            padding: '2px 6px',
            borderRadius: 12,
            fontSize: 11,
            background: 'transparent',
            border: '1px dashed var(--color-outline)',
            color: 'var(--color-element-mid)',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          <PlusIcon size={11} /> Agregar
        </button>
      ) : null}
    </div>
  );
}

type EstadoVista = 'inherit' | 'allow' | 'deny';

/**
 * Editor de permisos por VISTA de un usuario. Por cada vista del catálogo
 * permite: "Según rol" (hereda el default), "Permitir" (override allow) o
 * "Denegar" (override deny). Muestra en vivo si la vista queda visible. El
 * SuperAdmin ve todo siempre (los selects quedan deshabilitados).
 */
export function PermisosModal({
  usuario,
  saving,
  error,
  onGuardar,
  onCerrar,
}: {
  usuario: UsuarioListItem;
  saving: boolean;
  /** El error de guardar, si lo hubo: antes el modal quedaba abierto mudo. */
  error?: unknown;
  onGuardar: (allow: string[], deny: string[]) => void;
  onCerrar: () => void;
}) {
  const esSA = usuario.rol === UserRole.SUPERADMIN_MEDICUS;
  const defaults = new Set<ViewKey>(ROLE_DEFAULT_VIEWS[usuario.rol] ?? []);
  const etiqueta = new Map(PERMISSION_CATALOG.map((e) => [e.key, e.label]));

  /**
   * Qué dice la opción "Según rol" para una vista.
   *
   * Las sub-secciones (Rechazados, Alertas Bajas) no están en la semilla de
   * ningún rol: acompañan a su sección madre. Decir "no ve" ahí sería mentira —
   * un admin ve Rechazados porque ve Alta Grupos. Así que se nombra la madre.
   */
  const segunRol = (k: ViewKey): string => {
    const madre = VISTAS_DERIVADAS[k];
    if (madre) return `acompaña a ${etiqueta.get(madre) ?? madre}`;
    return defaults.has(k) ? 've' : 'no ve';
  };

  const [estado, setEstado] = useState<Record<string, EstadoVista>>(() => {
    const allow = new Set(usuario.overrides?.allow ?? []);
    const deny = new Set(usuario.overrides?.deny ?? []);
    const init: Record<string, EstadoVista> = {};
    for (const e of PERMISSION_CATALOG) {
      init[e.key] = deny.has(e.key) ? 'deny' : allow.has(e.key) ? 'allow' : 'inherit';
    }
    return init;
  });

  const allow = PERMISSION_CATALOG.filter((e) => estado[e.key] === 'allow').map((e) => e.key);
  const deny = PERMISSION_CATALOG.filter((e) => estado[e.key] === 'deny').map((e) => e.key);
  const efectivo = new Set(permisosEfectivos(usuario.rol, { allow, deny }));

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="rowBetween">
          <h2 className="heading-5">Permisos de {usuario.nombre}</h2>
          <button className="btn btn-ghost" style={{ padding: 4 }} onClick={onCerrar} aria-label="Cerrar">
            <XIcon size={16} />
          </button>
        </div>
        <p className="text-element-mid" style={{ marginTop: 4, fontSize: 13 }}>
          Rol: <strong>{ROL_LABELS[usuario.rol]}</strong>. Por default ve las vistas de su rol;
          acá podés forzar permitir o denegar vistas puntuales.
        </p>
        {esSA && (
          <p className="status status-warning" style={{ marginTop: 8 }}>
            El SuperAdmin ve todas las vistas siempre — los overrides no lo afectan.
          </p>
        )}

        <div style={{ marginTop: 12, overflowY: 'auto', flex: 1 }}>
          {PERMISSION_CATALOG.map((e) => {
            const visible = esSA || efectivo.has(e.key);
            return (
              <div
                key={e.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 150px 64px',
                  gap: 8,
                  alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: '1px solid var(--color-outline)',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {e.label}
                    {!e.navLabel && (
                      <span className="text-element-mid" style={{ fontSize: 11 }}> (sin menú)</span>
                    )}
                  </div>
                  <div className="text-element-mid" style={{ fontSize: 11 }}>
                    Según rol: {segunRol(e.key)}
                  </div>
                </div>
                <select
                  className="input"
                  style={{ padding: '4px 6px', fontSize: 12, height: 30 }}
                  value={estado[e.key]}
                  disabled={saving || esSA}
                  onChange={(ev) =>
                    setEstado((prev) => ({ ...prev, [e.key]: ev.target.value as EstadoVista }))
                  }
                >
                  <option value="inherit">Según rol ({segunRol(e.key)})</option>
                  <option value="allow">Permitir</option>
                  <option value="deny">Denegar</option>
                </select>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'right',
                    color: visible ? 'var(--color-success)' : 'var(--color-element-mid)',
                  }}
                >
                  {visible ? '✓ ve' : '✗ no ve'}
                </span>
              </div>
            );
          })}
        </div>

        <ErrorInline
          error={error}
          fallback="No se pudieron guardar los permisos."
          style={{ marginTop: 12 }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button className="btn btn-ghost" onClick={onCerrar} disabled={saving}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onGuardar(allow, deny)}
            disabled={saving || esSA}
          >
            <span className="btn-font-style">{saving ? 'Guardando…' : 'Guardar'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Cuentas de servicio: las integraciones que consumen la API con tokens m2m.
 *
 * No son usuarios y por eso viven en su propia pestaña: no se invitan por mail
 * ni tienen contraseña. Las crea sola el provisioning JIT la primera vez que el
 * tercero pide un token, y las crea INACTIVAS y sin organización a propósito —
 * habilitar una integración es una decisión, no un efecto secundario de que
 * alguien le pegue a la API.
 *
 * La pantalla existe porque esa decisión antes no se veía en ningún lado: el
 * scorecard del Growth Dashboard estuvo cinco días devolviendo 401 sin que nadie
 * se enterara. Por eso cada fila dice explícitamente QUÉ le falta.
 */
function CuentasServicioTab({
  cuentas,
  cargando,
  empresas,
  ocupado,
  onToggle,
  onAsignarEmpresa,
  onPermisos,
}: {
  cuentas?: CuentaServicio[];
  cargando: boolean;
  empresas: Empresa[];
  ocupado: boolean;
  onToggle: (id: string, activo: boolean) => void;
  onAsignarEmpresa: (usuarioId: string, empresaId: string) => void;
  onPermisos: (cuenta: CuentaServicio) => void;
}) {
  const nombreEmpresa = (id: string) =>
    empresas.find((e) => e.id === id)?.razonSocial ??
    empresas.find((e) => e.id === id)?.nombreComercial ??
    id;

  if (cargando) return <TableSkeleton rows={3} />;
  if (!cuentas?.length) {
    return (
      <div className="card" style={{ marginTop: '1rem' }}>
        <p className="text-element-mid" style={{ margin: 0, fontSize: 13 }}>
          Todavía no hay ninguna integración. Aparecen solas acá la primera vez que piden un
          token contra la API.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: '1rem', display: 'grid', gap: 10 }}>
      {cuentas.map((c) => {
        const listaParaOperar = c.falta.length === 0;
        return (
          <div
            key={c.id}
            style={{
              border: '1px solid var(--color-outline)',
              borderLeft: `3px solid ${listaParaOperar ? '#4caf50' : '#e8a33d'}`,
              borderRadius: 8,
              padding: '10px 12px',
              display: 'grid',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              {/* El `azp` es el nombre del client de Keycloak: es lo que
                  identifica a la integración. El `nombre` del usuario es el que
                  arma el JIT y no le dice nada a nadie. */}
              <strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{c.azp ?? c.nombre}</strong>
              <span className={listaParaOperar ? 'status status-success' : 'status status-warning'}>
                {listaParaOperar ? 'Operativa' : 'No puede operar'}
              </span>
              {c.rechazos > 0 && (
                <span
                  className="status status-error"
                  title={
                    c.ultimoRechazoAt
                      ? `Último rechazo: ${new Date(c.ultimoRechazoAt).toLocaleString('es-AR')}`
                      : undefined
                  }
                >
                  {c.rechazos} accesos rechazados
                </span>
              )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
              Rol del token: {c.rol}
              {c.creadoAt && ` · vista por primera vez el ${new Date(c.creadoAt).toLocaleDateString('es-AR')}`}
            </div>

            {c.falta.length > 0 && (
              <div style={{ fontSize: 12, color: '#8a5a00' }}>
                Le falta: {c.falta.includes('habilitar') && 'habilitarla'}
                {c.falta.length === 2 && ' y '}
                {c.falta.includes('organizacion') &&
                  (c.empresaIds.length > 1
                    ? 'dejar UNA sola organización'
                    : 'asignarle la organización')}
                . Hasta entonces sus llamadas reciben {c.activo ? '403' : '401'}.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12 }}>
                Organización:{' '}
                {c.empresaIds.length ? (
                  <strong>{c.empresaIds.map(nombreEmpresa).join(', ')}</strong>
                ) : (
                  <em style={{ color: 'var(--color-element-mid)' }}>sin asignar</em>
                )}
              </span>
              {c.empresaIds.length === 0 && (
                <select
                  className="input"
                  style={{ maxWidth: 260, fontSize: 12 }}
                  defaultValue=""
                  disabled={ocupado}
                  onChange={(e) => e.target.value && onAsignarEmpresa(c.id, e.target.value)}
                >
                  <option value="">Asignar organización…</option>
                  {empresas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.razonSocial ?? e.nombreComercial ?? e.id}
                    </option>
                  ))}
                </select>
              )}
              {/* Una integración no hereda NADA del rol: sin claves acá, la
                  cuenta se autentica bien y despues rechaza todo lo que intente
                  cargar. Es lo primero que hay que darle. */}
              <button
                type="button"
                className="btn btn-link btn--sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => onPermisos(c)}
              >
                Permisos
              </button>
              <button
                type="button"
                className="btn btn--sm"
                disabled={ocupado}
                onClick={() => onToggle(c.id, !c.activo)}
              >
                {c.activo ? 'Deshabilitar' : 'Habilitar'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Usuario de Binary del promotor, editable en línea. Se guarda en mayúsculas
 * porque así se compara contra Binary; el server hace lo mismo, esto es solo
 * para que lo que se ve sea lo que queda guardado.
 *
 * Vacío borra el dato: un usuario sin promotor no puede subir padrones.
 */
function PromotorCell({
  valor,
  onGuardar,
  busy,
}: {
  valor: string;
  onGuardar: (v: string) => void;
  busy: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const [txt, setTxt] = useState(valor);

  if (!editando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontFamily: valor ? 'monospace' : undefined, fontSize: 12.5 }}>
          {valor || <span style={{ color: 'var(--color-element-mid)' }}>sin promotor</span>}
        </span>
        <button
          className="btn btn-link"
          style={{ padding: 0, fontSize: 12 }}
          onClick={() => {
            setTxt(valor);
            setEditando(true);
          }}
        >
          {valor ? 'Cambiar' : 'Cargar'}
        </button>
      </div>
    );
  }

  const guardar = () => {
    onGuardar(txt.trim().toUpperCase());
    setEditando(false);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input
        autoFocus
        value={txt}
        maxLength={30}
        placeholder="PROMDIR"
        onChange={(e) => setTxt(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') guardar();
          if (e.key === 'Escape') setEditando(false);
        }}
        style={{ width: 120, fontFamily: 'monospace', fontSize: 12.5, padding: '2px 6px' }}
      />
      <button
        className="btn btn-link"
        style={{ padding: 0, fontSize: 12 }}
        disabled={busy}
        onClick={guardar}
      >
        Guardar
      </button>
      <button
        className="btn btn-link"
        style={{ padding: 0, fontSize: 12, color: 'var(--color-element-mid)' }}
        onClick={() => setEditando(false)}
      >
        Cancelar
      </button>
    </div>
  );
}
